import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import TrailBuddyChat from './TrailBuddy/TrailBuddyChat';
import '../styles/EventsCalendar.css';

const API_URL = (process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002').replace(/\/$/, '');

/* =========================
   Helpers tracking
========================= */

// Normalizza un numero per wa.me (rimuove tutto tranne cifre e +)
function toWaNumber(raw) {
    if (!raw) return null;
    let digits = String(raw).replace(/[^\d]/g, '');
    if (digits.startsWith('0039')) digits = '39' + digits.slice(4);
    if (digits.length === 10 && digits.startsWith('3')) digits = '39' + digits;
    // Verifica che sia mobile IT
    const isMobile = digits.startsWith('39')
        ? digits.slice(2).startsWith('3')
        : digits.startsWith('3');
    return isMobile ? digits : null;
}

// Fire-and-forget tracking generico (non blocca mai l'azione utente)
function trackContact(type, eventId) {
    if (!eventId) return;
    fetch(`${API_URL}/go/event-contact?type=${type}&eventId=${encodeURIComponent(eventId)}`,
        { method: 'GET', keepalive: true }
    ).catch(() => { }); // silenzioso
}

/* =========================
   OrganizerContacts
   Gestisce WhatsApp + tel + email con tracking
========================= */
function OrganizerContacts({ phone, email, eventId }) {
    if (!phone && !email) return null;

    const waNumber = toWaNumber(phone);

    const handleWhatsApp = () => {
        trackContact('whatsapp', eventId);
        const text = encodeURIComponent('Ciao, ti contatto tramite AI Hiking per il tuo evento!');
        window.open(`https://wa.me/${waNumber}?text=${text}`, '_blank', 'noopener,noreferrer');
    };

    const handlePhone = () => {
        trackContact('phone', eventId);
        window.location.href = `tel:${phone}`;
    };

    const handleEmail = () => {
        trackContact('email', eventId);
        window.location.href = `mailto:${email}`;
    };

    const btnStyle = {
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
        fontWeight: '600', cursor: 'pointer', border: 'none',
        textDecoration: 'none', marginRight: '8px', marginBottom: '6px'
    };

    return (
        <div className="organizer-contacts" style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {/* WhatsApp — solo se il numero è un mobile italiano */}
            {phone && waNumber && (
                <button
                    style={{ ...btnStyle, background: '#25D366', color: 'white' }}
                    onClick={handleWhatsApp}
                    title="Scrivi su WhatsApp"
                >
                    💬 WhatsApp
                </button>
            )}

            {/* Tel — sempre visibile se c'è un numero (fisso o mobile) */}
            {phone && (
                <button
                    style={{ ...btnStyle, background: '#2196F3', color: 'white' }}
                    onClick={handlePhone}
                    title={`Chiama ${phone}`}
                >
                    📞 {phone}
                </button>
            )}

            {/* Email */}
            {email && (
                <button
                    style={{ ...btnStyle, background: '#FF9800', color: 'white' }}
                    onClick={handleEmail}
                    title={`Scrivi a ${email}`}
                >
                    ✉️ {email}
                </button>
            )}
        </div>
    );
}

/* =========================
   Componente principale
========================= */
export default function EventDetailModal({ event, onClose, onEventUpdated }) {
    const { user, isAuthenticated, loginWithGoogle, loading: authLoading } = useAuth();

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const hasLoadedWithAuth = useRef(false);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('it-IT', {
            weekday: 'short', day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    const loadEventDetail = useCallback(async (eventId, forceAuth = false) => {
        setDetailLoading(true);
        try {
            const headers = {};
            if ((isAuthenticated || forceAuth) && user) {
                const token = await user.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
                hasLoadedWithAuth.current = true;
            }
            const isActivity = event?.eventType === 'activity';
            const endpoint = isActivity
                ? `${API_URL}/api/events/${eventId}`
                : `${API_URL}/api/trail-buddy/${eventId}`;

            const res = await fetch(endpoint, { headers });
            if (res.ok) {
                const data = await res.json();
                setSelectedEvent(data);
            } else {
                setSelectedEvent(event);
            }
        } catch (err) {
            console.error('Errore caricamento dettaglio:', err);
            setSelectedEvent(event);
        } finally {
            setDetailLoading(false);
        }
    }, [isAuthenticated, user, event]);

    useEffect(() => {
        if (!event) return;
        const eventId = event._id || event.id;
        if (eventId) loadEventDetail(eventId);
        else setSelectedEvent(event);
    }, [event]);

    useEffect(() => {
        if (isAuthenticated && user && !hasLoadedWithAuth.current) {
            const eventId = event?._id || event?.id;
            if (eventId) loadEventDetail(eventId, true);
        }
    }, [isAuthenticated, user]);

    const handleGoogleLogin = async () => {
        setIsLoggingIn(true);
        setActionError(null);
        try {
            await loginWithGoogle();
            hasLoadedWithAuth.current = false;
        } catch {
            setActionError('Login Google fallito. Riprova.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const ensureUserProfile = async (firebaseUser) => {
        try {
            const token = await firebaseUser.getIdToken();
            await fetch(`${API_URL}/api/trail-buddy/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ displayName: firebaseUser.displayName || '', email: firebaseUser.email || '', phone: '' })
            });
        } catch (err) {
            console.error('Errore creazione profilo:', err);
        }
    };

    const handleJoin = async () => {
        if (!isAuthenticated || !user) return;
        setActionLoading(true); setActionError(null);
        try {
            await ensureUserProfile(user);
            const token = await user.getIdToken();
            const res = await fetch(`${API_URL}/api/trail-buddy/${selectedEvent._id}/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) {
                if (data.currentStatus) { setSelectedEvent(prev => ({ ...prev, myStatus: data.currentStatus, canJoin: false })); return; }
                throw new Error(data.error || 'Errore');
            }
            setSelectedEvent(prev => ({ ...prev, myStatus: 'pending', canJoin: false }));
            await loadEventDetail(selectedEvent._id, true);
            if (onEventUpdated) onEventUpdated();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const handleWithdraw = async () => {
        if (!isAuthenticated || !user) return;
        if (!window.confirm('Vuoi ritirarti da questo evento?')) return;
        setActionLoading(true); setActionError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${API_URL}/api/trail-buddy/${selectedEvent._id}/join`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Errore');
            await loadEventDetail(selectedEvent._id, true);
            if (onEventUpdated) onEventUpdated();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const handleActivityJoin = async () => {
        if (!isAuthenticated || !user) return;
        setActionLoading(true); setActionError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${API_URL}/api/events/${selectedEvent._id}/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Errore');
            setSelectedEvent(prev => ({ ...prev, myStatus: 'joined', participantsCount: (prev.participantsCount || 0) + 1 }));
            if (onEventUpdated) onEventUpdated();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const handleActivityWithdraw = async () => {
        if (!isAuthenticated || !user) return;
        if (!window.confirm('Vuoi ritirarti da questo evento?')) return;
        setActionLoading(true); setActionError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${API_URL}/api/events/${selectedEvent._id}/join`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Errore');
            setSelectedEvent(prev => ({ ...prev, myStatus: null, participantsCount: Math.max(0, (prev.participantsCount || 1) - 1) }));
            if (onEventUpdated) onEventUpdated();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const handleCancelEvent = async () => {
        if (!window.confirm('Sei sicuro di voler cancellare questo evento?')) return;
        setActionLoading(true); setActionError(null);
        try {
            const token = await user.getIdToken();
            const isActivity = selectedEvent.eventType === 'activity';
            const endpoint = isActivity
                ? `${API_URL}/api/events/${selectedEvent._id}`
                : `${API_URL}/api/trail-buddy/${selectedEvent._id}`;
            const res = await fetch(endpoint, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Errore');
            alert('✅ Evento cancellato');
            if (onEventUpdated) onEventUpdated();
            onClose();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const handleResolveParticipant = async (participantUid, action) => {
        setActionLoading(true); setActionError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch(`${API_URL}/api/trail-buddy/${selectedEvent._id}/participants/${participantUid}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Errore');
            await loadEventDetail(selectedEvent._id, true);
            if (onEventUpdated) onEventUpdated();
        } catch (err) { setActionError(err.message); }
        finally { setActionLoading(false); }
    };

    const GoogleLoginButton = () => (
        <div className="login-to-join">
            <p>Accedi con Google per partecipare</p>
            <button className="btn-google-login" onClick={handleGoogleLogin} disabled={isLoggingIn || authLoading}>
                {isLoggingIn ? '⏳ Accesso...' : (
                    <>
                        <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Accedi con Google
                    </>
                )}
            </button>
        </div>
    );

    if (!event) return null;

    if (showChat && selectedEvent) {
        return (
            <div style={{ position: 'fixed', inset: 0, background: 'white', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
                <div style={{ padding: '12px 16px', background: '#388E3C', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                    <button onClick={() => setShowChat(false)} style={{ background: 'white', color: '#388E3C', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
                        ← Torna all'evento
                    </button>
                    <span style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{selectedEvent.eventName}</span>
                </div>
                <TrailBuddyChat eventId={selectedEvent._id} eventName={selectedEvent.eventName} onClose={() => setShowChat(false)} />
            </div>
        );
    }

    if (detailLoading || !selectedEvent) {
        return (
            <div className="event-detail-overlay" onClick={onClose}>
                <div className="event-detail-modal" onClick={e => e.stopPropagation()}>
                    <button className="close-button" onClick={onClose}>✕</button>
                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>⏳ Caricamento dettagli evento...</div>
                </div>
            </div>
        );
    }

    const isActivity = selectedEvent.eventType === 'activity';

    return (
        <div className="event-detail-overlay" onClick={onClose}>
            <div className="event-detail-modal" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>✕</button>

                <div className={`event-type-badge ${selectedEvent.eventType}`}>
                    {isActivity ? t('activity_event', '✨ Evento Attività') : `🥾👥 ${t('find_buddies')}`}
                </div>

                <h2>{selectedEvent.eventName}</h2>

                {/* Quando */}
                <div className="detail-section">
                    <h3>📅 Quando</h3>
                    <p>
                        {formatDate(selectedEvent.eventDate)}
                        {selectedEvent.privateDetails?.meetingTime
                            ? ` alle ${selectedEvent.privateDetails.meetingTime}`
                            : selectedEvent.meetingTime ? ` alle ${selectedEvent.meetingTime}` : ''}
                    </p>
                </div>

                {/* Percorso / POI */}
                <div className="detail-section">
                    <h3>🗺️ Percorso</h3>
                    {selectedEvent.trail && (
                        <>
                            <p><strong>{selectedEvent.trail.name}</strong></p>
                            {selectedEvent.trail.length && <span>📏 {selectedEvent.trail.length} </span>}
                            {selectedEvent.trail.ascent && <span>⛰️ {selectedEvent.trail.ascent}m </span>}
                            {selectedEvent.trail.difficulty && <span>💪 {selectedEvent.trail.difficulty}</span>}
                            {selectedEvent.trail.id && selectedEvent.trail.region && (
                                <div style={{ marginTop: 8 }}>
                                    <a href={`/region/${selectedEvent.trail.region}/trekkings?trailId=${encodeURIComponent(selectedEvent.trail.id)}`}
                                        style={{ display: 'inline-block', padding: '6px 14px', background: '#4CAF50', color: 'white', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                                        🗺️ Vai
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                    {selectedEvent.poi && (
                        <>
                            <p><strong>{selectedEvent.poi.name}</strong></p>
                            {selectedEvent.poi.id && selectedEvent.poi.region && (
                                <a href={`/region/${selectedEvent.poi.region}/experiences?poiId=${encodeURIComponent(selectedEvent.poi.id)}`}
                                    style={{ display: 'inline-block', marginTop: 8, padding: '6px 14px', background: '#4CAF50', color: 'white', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                                    🗺️ Vai
                                </a>
                            )}
                        </>
                    )}
                </div>

                {/* Punto di ritrovo */}
                {(selectedEvent.privateDetails?.meetingPoint || selectedEvent.meetingPoint) && (
                    <div className="detail-section">
                        <h3>📍 Punto di Ritrovo</h3>
                        <p>{selectedEvent.privateDetails?.meetingPoint || selectedEvent.meetingPoint}</p>
                    </div>
                )}

                {/* Posti — Trail Buddy */}
                {!isActivity && (
                    <div className="detail-section">
                        <h3>👥 Posti</h3>
                        {selectedEvent.lookingFor ? (
                            <p>
                                Cerca {selectedEvent.lookingFor} {selectedEvent.lookingFor === 1 ? 'persona' : 'persone'}
                                {selectedEvent.spotsLeft != null && (
                                    <span className={`spots-inline ${selectedEvent.spotsLeft <= 0 ? 'full' : ''}`}>
                                        {' – '}{selectedEvent.spotsLeft > 0
                                            ? `${selectedEvent.spotsLeft} ${selectedEvent.spotsLeft === 1 ? 'posto disponibile' : 'posti disponibili'}`
                                            : 'Completo'}
                                    </span>
                                )}
                            </p>
                        ) : <p>Aperto a tutti</p>}
                    </div>
                )}

                {/* Partecipanti — Activity */}
                {isActivity && (
                    <div className="detail-section">
                        <h3>👥 Partecipanti</h3>
                        <p>
                            {selectedEvent.participantsCount > 0
                                ? `${selectedEvent.participantsCount} ${selectedEvent.participantsCount === 1 ? 'persona iscritta' : 'persone iscritte'}`
                                : 'Nessun iscritto ancora — sii il primo!'}
                            {selectedEvent.maxParticipants ? ` (max ${selectedEvent.maxParticipants})` : ''}
                        </p>
                    </div>
                )}

                {/* Note / Descrizione */}
                {(selectedEvent.privateDetails?.notesFull || selectedEvent.publicSummary || selectedEvent.notes || selectedEvent.description) && (
                    <div className="detail-section">
                        <h3>📝 Note</h3>
                        <p>{selectedEvent.privateDetails?.notesFull || selectedEvent.description || selectedEvent.publicSummary || selectedEvent.notes}</p>
                    </div>
                )}

                {/* Organizzatore */}
                <div className="detail-section">
                    <h3>👤 Organizzatore</h3>
                    <div className="organizer-info">
                        {(selectedEvent.proposerPhoto || selectedEvent.organizerPhoto) && (
                            <img src={selectedEvent.proposerPhoto || selectedEvent.organizerPhoto}
                                alt="" className="organizer-photo" referrerPolicy="no-referrer" />
                        )}
                        <span>{selectedEvent.proposerDisplayName || selectedEvent.organizerName || 'Utente'}</span>
                    </div>

                    {/* ✅ Contatti organizzatore con tracking WhatsApp / tel / email */}
                    {isActivity && (selectedEvent.organizerPhone || selectedEvent.organizerEmail) && (
                        <OrganizerContacts
                            phone={selectedEvent.organizerPhone}
                            email={selectedEvent.organizerEmail}
                            eventId={selectedEvent._id}
                        />
                    )}
                </div>

                {/* Contatti gruppo — Trail Buddy */}
                {!isActivity && selectedEvent.contacts?.length > 0 && (
                    <div className="detail-section contacts-section">
                        <h3>📱 Contatti del Gruppo</h3>
                        <p className="contacts-hint">Visibili solo ai partecipanti accettati</p>
                        {selectedEvent.contacts.map(contact => (
                            <div key={contact.uid} className="contact-card">
                                {contact.photoURL && <img src={contact.photoURL} alt="" className="contact-photo" referrerPolicy="no-referrer" />}
                                <div className="contact-details">
                                    <span className="contact-name">
                                        {contact.displayName}
                                        {contact.role === 'proposer' && <span className="contact-role"> (organizzatore)</span>}
                                    </span>
                                    {/* Trail Buddy usa wa.me diretto (numeri inseriti dall'utente) */}
                                    {contact.phone && (
                                        <a href={`https://wa.me/${contact.phone.replace(/[^0-9+]/g, '')}`}
                                            className="contact-whatsapp" target="_blank" rel="noopener noreferrer">
                                            📱 {contact.phone}
                                        </a>
                                    )}
                                    {contact.email && (
                                        <a href={`mailto:${contact.email}`} className="contact-email">✉️ {contact.email}</a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Gestione partecipanti — solo Trail Buddy owner */}
                {!isActivity && selectedEvent.isOwner && selectedEvent.participants?.length > 0 && (
                    <div className="detail-section participants-section">
                        <h3>👥 Richieste di Partecipazione</h3>
                        {selectedEvent.participants.map(p => (
                            <div key={p.uid} className={`participant-card status-${p.status}`}>
                                <div className="participant-info">
                                    <span className="participant-name">{p.displayName}</span>
                                    <span className={`participant-status ${p.status}`}>
                                        {p.status === 'pending' && '⏳ In attesa'}
                                        {p.status === 'accepted' && '✅ Accettato'}
                                        {p.status === 'rejected' && '❌ Rifiutato'}
                                        {p.status === 'withdrawn' && '🚪 Ritirato'}
                                    </span>
                                </div>
                                {p.status === 'pending' && (
                                    <div className="participant-actions">
                                        <button className="btn-accept" onClick={() => handleResolveParticipant(p.uid, 'accept')} disabled={actionLoading}>✅ Accetta</button>
                                        <button className="btn-reject" onClick={() => handleResolveParticipant(p.uid, 'reject')} disabled={actionLoading}>❌ Rifiuta</button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Lista iscritti — Activity owner */}
                {isActivity && selectedEvent.isOwner && selectedEvent.participants?.length > 0 && (
                    <div className="detail-section participants-section">
                        <h3>👥 Iscritti ({selectedEvent.participants.length})</h3>
                        {selectedEvent.participants.map(p => (
                            <div key={p.uid} className="participant-card status-accepted">
                                <div className="participant-info">
                                    {p.photoURL && <img src={p.photoURL} alt="" className="contact-photo" referrerPolicy="no-referrer" />}
                                    <span className="participant-name">{p.displayName}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {actionError && <div className="action-error" style={{ margin: '12px 0' }}>⚠️ {actionError}</div>}

                {/* Azioni owner */}
                {selectedEvent.isOwner && (
                    <div className="detail-actions" style={{ borderTop: '2px solid #ffcdd2', paddingTop: '16px', marginTop: '16px' }}>
                        {!isActivity && selectedEvent.chatId && (
                            <button onClick={() => setShowChat(true)} style={{ width: '100%', padding: '12px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' }}>
                                💬 Apri chat gruppo
                            </button>
                        )}
                        <button onClick={handleCancelEvent} disabled={actionLoading} style={{ width: '100%', padding: '12px', background: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading ? '⏳ Cancellazione...' : '🗑️ Cancella Evento'}
                        </button>
                    </div>
                )}

                {/* Azioni utente — Activity */}
                {isActivity && !selectedEvent.isOwner && (
                    <div className="detail-actions">
                        {!isAuthenticated ? <GoogleLoginButton />
                            : selectedEvent.myStatus === 'joined' ? (
                                <div className="status-message accepted">
                                    <p>✅ Sei iscritto a questo evento!</p>
                                    <button className="btn-withdraw" onClick={handleActivityWithdraw} disabled={actionLoading}>
                                        {actionLoading ? '⏳...' : '🚪 Disiscriviti'}
                                    </button>
                                </div>
                            ) : selectedEvent.maxParticipants && selectedEvent.participantsCount >= selectedEvent.maxParticipants ? (
                                <div className="status-message full"><p>🔴 Evento al completo</p></div>
                            ) : (
                                <button className="btn-join" onClick={handleActivityJoin} disabled={actionLoading}>
                                    {actionLoading ? '⏳ Iscrizione...' : '🙋 Partecipo!'}
                                </button>
                            )}
                    </div>
                )}

                {/* Azioni utente — Trail Buddy */}
                {!isActivity && !selectedEvent.isOwner && (
                    <div className="detail-actions">
                        {!isAuthenticated ? <GoogleLoginButton />
                            : selectedEvent.myStatus === null && selectedEvent.canJoin ? (
                                <button className="btn-join" onClick={handleJoin} disabled={actionLoading}>
                                    {actionLoading ? '⏳ Invio richiesta...' : '🤝 Voglio Partecipare!'}
                                </button>
                            ) : selectedEvent.myStatus === 'pending' ? (
                                <div className="status-message pending">
                                    <p>⏳ Richiesta inviata – in attesa di conferma</p>
                                    <button className="btn-withdraw" onClick={handleWithdraw} disabled={actionLoading}>Ritirati</button>
                                </div>
                            ) : selectedEvent.myStatus === 'accepted' ? (
                                <div className="status-message accepted">
                                    <p>✅ Sei stato accettato! Ci vediamo sul sentiero!</p>
                                    {selectedEvent.chatId && (
                                        <button className="btn-join" onClick={() => setShowChat(true)}>💬 Apri chat gruppo</button>
                                    )}
                                    <button className="btn-withdraw" onClick={handleWithdraw} disabled={actionLoading}>Ritirati dall'evento</button>
                                </div>
                            ) : selectedEvent.myStatus === 'rejected' ? (
                                <div className="status-message rejected"><p>😔 La tua richiesta non è stata accettata</p></div>
                            ) : selectedEvent.myStatus === 'withdrawn' ? (
                                <div className="status-message withdrawn"><p>🚪 Ti sei ritirato da questo evento</p></div>
                            ) : selectedEvent.spotsLeft <= 0 ? (
                                <div className="status-message full"><p>🔴 Evento al completo</p></div>
                            ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}
